const AuroraCarousel = (function () {

  // CDN implementation notes here: https://www.embla-carousel.com/get-started/cdn/

  const settings = {
    name: "aurora-carousel.js",
    debugging: true,
  };

  const elements = document.querySelectorAll(".js--embla") || [];

  const getSlideCount = function (element = false) {
    if (!element) {
      return 0;
    }

    const container = element.querySelector(".embla__container--main");

    if (!container) {
      return 0;
    }

    return container.querySelectorAll(".embla__slide").length;
  };

  const canInitializeCarousel = function (element = false) {
    if (!element) {
      return false;
    }

    const viewport = element.querySelector(".embla__viewport--main");

    if (!viewport) {
      return false;
    }

    if (0 === getSlideCount(element)) {
      return false;
    }

    return viewport.getBoundingClientRect().width > 0;
  };

  const addCarouselEventHandlers = function (carouselApi = false, carouselThumbsApi = false) {
    const slidesThumbs = carouselThumbsApi ? carouselThumbsApi.slideNodes() : [];

    const updateStates = () => {
      const root = carouselApi.rootNode();
      const navButtons = root.closest(".embla__main").querySelectorAll(".embla__nav") || [];
      const previousIndex = carouselApi.previousScrollSnap();
      const selectedIndex = carouselApi.selectedScrollSnap();

      if (carouselThumbsApi) {
        const previousSlide = slidesThumbs[previousIndex];
        const selectedSlide = slidesThumbs[selectedIndex];

        carouselThumbsApi.scrollTo(selectedIndex);

        if (previousSlide) {
          previousSlide.classList.remove("embla__slide--selected");
        }

        if (selectedSlide) {
          selectedSlide.classList.add("embla__slide--selected");
        }
      }

      navButtons.forEach((button) => {
        button.classList.remove("at-start", "at-end");
        if (!carouselApi.canScrollPrev() && button.classList.contains("embla__nav--prev")) {
          button.classList.add("at-start");
        }
        if (!carouselApi.canScrollNext() && button.classList.contains("embla__nav--next")) {
          button.classList.add("at-end");
        }
      });
    };

    const updateAutoPlay = () => {
      const plugins = carouselApi.plugins();
      const autoplay = plugins?.autoplay || false;

      if (autoplay && !autoplay.isPlaying()) {
        autoplay.play();
      }
    };

    carouselApi.on("init", updateStates);
    carouselApi.on("reInit", updateStates);
    carouselApi.on("select", () => {
      updateStates();
      updateAutoPlay();
    });

    if (carouselThumbsApi) {
      carouselThumbsApi.on("init", updateStates);
      carouselThumbsApi.on("reInit", updateStates);
    }
  };

  const addNavigationClickHandlers = function (carouselApi = false) {
    if (carouselApi) {
      const root = carouselApi.rootNode();
      const navButtons = root.closest(".embla__main").querySelectorAll(".embla__nav") || [];

      const handleClick = (event) => {
        const button = event.target.closest(".button");

        if (!button) {
          return;
        }

        if (button.classList.contains("embla__nav--prev")) {
          carouselApi.scrollPrev();
        }

        if (button.classList.contains("embla__nav--next")) {
          carouselApi.scrollNext();
        }

        navButtons.forEach((button) => {
          button.classList.remove("at-start", "at-end");
          if (!carouselApi.canScrollPrev() && button.classList.contains("embla__nav--prev")) {
            button.classList.add("at-start");
          }
          if (!carouselApi.canScrollNext() && button.classList.contains("embla__nav--next")) {
            button.classList.add("at-end");
          }
        });
      };

      navButtons.forEach((button) => button.addEventListener("click", handleClick, false));
    }
  };

  const addThumbnailClickHandlers = function (carouselApi = false, carouselThumbsApi = false) {
    if (carouselApi && carouselThumbsApi) {
      const slidesThumbs = carouselThumbsApi.slideNodes();

      const scrollToIndex = slidesThumbs.map((_, index) => () => carouselApi.scrollTo(index));

      slidesThumbs.forEach((slideNode, index) => {
        slideNode.addEventListener("click", scrollToIndex[index], false);
      });
    }
  };

  const optionsFactory = function (custom = {}) {
    const standard = {
      loop: false,
    };

    return { ...standard, ...custom };
  };

  const initCarousel = function (element = false, shouldReInitialize = false) {
    if (!canInitializeCarousel(element) || "function" !== typeof EmblaCarousel) {
      return false;
    }

    const existingInstance = element.auroraCarouselInstance || false;

    if (existingInstance) {
      if (shouldReInitialize) {
        existingInstance.carouselApi.reInit();

        if (existingInstance.carouselThumbsApi) {
          existingInstance.carouselThumbsApi.reInit();
        }
      }

      return true;
    }

    let carouselApi = false;
    let carouselThumbsApi = false;
    let options = {};
    let optionsThumbs = {};

    const carousel = element.querySelector(".embla__viewport--main");
    const carouselThumbs = element.querySelector(".embla__viewport--thumbs");
    const carouselType = element.getAttribute("data-carousel-type");
    const autoplayDelay = parseInt(element.getAttribute("data-carousel-autoplay-delay") || 2500);

    // conditionally set options based on "type"
    switch (carouselType) {
      case "announcements": {
        options = optionsFactory({
          duration: 35, // Only values between 20-60 are recommended.
          loop: true,
        });
        break;
      }
      case "main-product": {
        options = optionsFactory({});
        if (carouselThumbs) {
          optionsThumbs = optionsFactory({
            containScroll: "keepSnaps",
            dragFree: true,
          });
        }
        break;
      }
      case "loading-overlay": {
        options = optionsFactory({
          loop: true,
          draggable: false,
        });
        break;
      }
      case "quiz": {
        options = optionsFactory({
          duration: 35, // Only values between 20-60 are recommended.
          loop: false,
          watchDrag: false
        });
        break;
      }
      default: {
        options = optionsFactory({});
        break;
      }
    }

    // initialize carousel and attach carousel event handlers
    // plugin features must be prefixed with "EmblaCarousel" when loading via CDN
    if ("announcements" === carouselType || "loading-overlay" === carouselType) {
      carouselApi = EmblaCarousel(carousel, options, [EmblaCarouselAutoplay({ delay: autoplayDelay, stopOnInteraction: false })]);
    } else {
      carouselApi = EmblaCarousel(carousel, options);
    }

    carouselThumbsApi = carouselThumbs ? EmblaCarousel(carouselThumbs, optionsThumbs) : false;

    // add thumbnail functionality with "click" handlers
    addThumbnailClickHandlers(carouselApi, carouselThumbsApi);

    // add navigation functionality with "click" handlers
    addNavigationClickHandlers(carouselApi);

    // add carousel event handlers
    addCarouselEventHandlers(carouselApi, carouselThumbsApi);

    element.auroraCarouselInstance = {
      carouselApi,
      carouselThumbsApi,
    };

    return true;
  };

  const initElement = function (element = false) {
    return initCarousel(element, true);
  };

  const init = function () {
    if (settings.debugging) console.log(`${settings.name} :: Start`);
    elements.forEach((element) => initCarousel(element));
    if (settings.debugging) console.log(`${settings.name} :: Complete`);
  };

  return {
    init,
    initElement,
    settings,
  };

})();
